import React from 'react';
import { Check, AlertCircle } from 'lucide-react';

interface FieldOption {
  name: string;
  value: string;
}

interface FieldSelectorProps {
  label: string;
  availableFields: FieldOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  error?: string;
}

const FieldSelector: React.FC<FieldSelectorProps> = ({
  label,
  availableFields,
  selectedValues,
  onChange,
  error
}) => {
  const toggleField = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const selectAll = () => {
    onChange(availableFields.map(f => f.value));
  };

  const deselectAll = () => {
    onChange([]);
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px'
      }}>
        <label style={{
          fontSize: '13px',
          fontWeight: '500',
          color: '#374151'
        }}>
          {label}
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={selectAll}
            style={{
              fontSize: '11px',
              color: '#3b82f6',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0'
            }}
          >
            Select All
          </button>
          <button
            type="button"
            onClick={deselectAll}
            style={{
              fontSize: '11px',
              color: '#3b82f6',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0'
            }}
          >
            Deselect All
          </button>
        </div>
      </div>

      <div style={{
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        backgroundColor: '#ffffff',
        maxHeight: '200px',
        overflowY: 'auto'
      }}>
        {availableFields.map((field) => {
          const isSelected = selectedValues.includes(field.value);
          return (
            <label
              key={field.value}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #f3f4f6',
                transition: 'background-color 0.15s',
                backgroundColor: isSelected ? '#eff6ff' : '#ffffff'
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isSelected ? '#eff6ff' : '#ffffff';
              }}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleField(field.value)}
                style={{ display: 'none' }}
              />
              <div style={{
                width: '18px',
                height: '18px',
                border: `2px solid ${isSelected ? '#3b82f6' : '#d1d5db'}`,
                borderRadius: '4px',
                marginRight: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isSelected ? '#3b82f6' : '#ffffff',
                transition: 'all 0.15s'
              }}>
                {isSelected && <Check size={12} color="#ffffff" strokeWidth={3} />}
              </div>
              <span style={{
                fontSize: '13px',
                color: '#374151',
                fontWeight: isSelected ? '500' : '400'
              }}>
                {field.name}
              </span>
            </label>
          );
        })}
      </div>

      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          marginTop: '6px',
          fontSize: '12px',
          color: '#dc2626'
        }}>
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default FieldSelector;
